// The progression prototype's app state: the signed-in player's document,
// their wars, and which screen is up. Every change goes through the server
// (data/prototypeData.js); this only holds what the server last said.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useAuthFromContext } from '../../AuthContext'
import * as data from '../../data/prototypeData'

const PrototypeContext = createContext(null)
export const usePrototype = () => useContext(PrototypeContext)

// An invite link is the site with ?invite=CODE.
export const inviteLink = (code) => `${window.location.origin}/?invite=${code}`

function inviteFromUrl() {
    const code = new URLSearchParams(window.location.search).get('invite')
    return code ? code.toUpperCase() : null
}

// A war link (what a push notification or a nudge opens) is the site with ?war=ID.
export const warLink = (id) => `${window.location.origin}/?war=${encodeURIComponent(id)}`
const warFromUrl = () => new URLSearchParams(window.location.search).get('war')

export function PrototypeProvider({ children }) {
    const { user } = useAuthFromContext()
    const uid = user?.uid ?? null
    const [player, setPlayerState] = useState(null)
    // Every copy of the player that arrives (the listener, an edit's answer, a
    // fetch) counts here, so a fetch that was overtaken while in flight is
    // dropped instead of undoing what overtook it.
    const playerSeq = useRef(0)
    const setPlayer = useCallback((p) => { playerSeq.current++; setPlayerState(p) }, [])
    const [wars, setWars] = useState([])
    // { name: 'home' } | { name: 'warMenu' } | { name: 'waiting', challenge: { code, mode, generated, expiresAt } } | { name: 'history' } | { name: 'tutorial' } | { name: 'hive', placement?, retry?, firstMill? } | { name: 'war', warId }
    const [screen, setScreen] = useState({ name: 'home' })
    const [notice, setNotice] = useState(null)        // one line of feedback for the home and war menu screens
    const [pendingInvite, setPendingInvite] = useState(inviteFromUrl)
    const [pendingWar, setPendingWar] = useState(warFromUrl)
    const [warsLoaded, setWarsLoaded] = useState(false)

    // The player fetched fresh (creating the document on first sight). The
    // listener below normally keeps it current, but in an iPhone home-screen
    // app it can go quiet (after the app has been in the background), leaving
    // screens on an old copy: so it's also fetched on every screen change and
    // whenever the app comes back to the front.
    const refreshPlayer = useCallback((onError = () => {}) => {
        if (!uid) return
        const at = playerSeq.current
        data.loadPlayer().then(p => { if (playerSeq.current === at) setPlayer(p) }).catch(onError)
    }, [uid, setPlayer])

    useEffect(() => {
        if (!uid) return undefined
        refreshPlayer(e => setNotice(e.message))
        const unPlayer = data.watchPlayer(uid, (p) => { if (p) setPlayer(p) })
        return () => unPlayer()
    }, [uid, refreshPlayer, setPlayer])

    // The wars' listener can go quiet the same way, and a challenger leaves the
    // app to share their link, then waits on this: so it's started over
    // whenever the app comes back to the front.
    useEffect(() => {
        if (!uid) return undefined
        const watch = () => data.watchMyWars(uid, (w) => { setWars(w); setWarsLoaded(true) })
        let unWars = watch()
        const onShow = () => {
            if (document.visibilityState !== 'visible') return
            unWars()
            unWars = watch()
        }
        document.addEventListener('visibilitychange', onShow)
        return () => { document.removeEventListener('visibilitychange', onShow); unWars() }
    }, [uid])

    useEffect(() => { refreshPlayer() }, [screen.name, refreshPlayer])
    useEffect(() => {
        const onShow = () => { if (document.visibilityState === 'visible') refreshPlayer() }
        document.addEventListener('visibilitychange', onShow)
        return () => document.removeEventListener('visibilitychange', onShow)
    }, [refreshPlayer])

    // A war link opens its war once the player's wars are in, if it's one
    // of theirs; the link leaves the address bar either way.
    useEffect(() => {
        if (!pendingWar || !warsLoaded) return
        if (wars.some(w => w.id === pendingWar)) setScreen({ name: 'war', warId: pendingWar })
        else setNotice('That war isn’t one of yours.')
        setPendingWar(null)
        window.history.replaceState(null, '', window.location.pathname)
    }, [pendingWar, warsLoaded, wars])

    // A war needs a Hive ready for it; the server says what's missing, and the
    // Hive editor opens on the placement step. `retry` is what to try again
    // from there once the Hive is ready.
    const handleWarError = useCallback((e, retry) => {
        const placement = e.details?.placement
        if (e.code === 'failed-precondition' && placement) {
            setScreen({ name: 'hive', placement, retry })
            return
        }
        setNotice(e.message)
    }, [])

    // The scripted tutorial (TutorialScreen); finishing it opens the Genesis Chest.
    const startTutorial = useCallback(() => {
        setNotice(null)
        setScreen({ name: 'tutorial' })
    }, [])

    // Skipping the tutorial grants its Genesis Chest with no reveal: the home
    // screen opens up and the contents wait in the inventory, to be placed.
    const skipTutorial = useCallback(async () => {
        setNotice(null)
        try {
            await data.openGenesisChest()
            setPlayer(await data.loadPlayer())
        } catch (e) { setNotice(e.message) }
    }, [setPlayer])

    // `generated`: a test war on generated Hives of that many structures.
    const startBotWar = useCallback(async (generated = null) => {
        setNotice(null)
        try {
            const { warId } = await data.startBotWar(generated)
            setScreen({ name: 'war', warId })
        } catch (e) { handleWarError(e, { kind: 'bot' }) }
    }, [handleWarError])

    // Opens the challenge's waiting room, or when it couldn't be made, the
    // notice says why or the placement step opens.
    const createChallenge = useCallback(async (mode, generated = null) => {
        setNotice(null)
        try {
            const { code, expiresAt } = await data.createChallenge(mode, generated)
            setScreen({ name: 'waiting', challenge: { code, mode, generated, expiresAt } })
        } catch (e) { handleWarError(e, { kind: 'challenge', mode }) }
    }, [handleWarError])

    const acceptChallenge = useCallback(async (code) => {
        setNotice(null)
        try {
            const { warId } = await data.acceptChallenge(code)
            setPendingInvite(null)
            window.history.replaceState(null, '', window.location.pathname)
            setScreen({ name: 'war', warId })
        } catch (e) { handleWarError(e, { kind: 'accept', code }) }
    }, [handleWarError])

    // Back from the placement step: tries the war again.
    const resume = useCallback(async (retry) => {
        if (retry?.kind === 'bot') return startBotWar()
        if (retry?.kind === 'accept') return acceptChallenge(retry.code)
        if (retry?.kind === 'challenge') return createChallenge(retry.mode)
        setScreen({ name: 'warMenu' })
        return undefined
    }, [startBotWar, acceptChallenge, createChallenge])

    const value = {
        uid, player, wars, screen, notice, pendingInvite,
        setScreen, setNotice, setPendingInvite, setPlayer,
        startTutorial, skipTutorial, startBotWar, createChallenge, acceptChallenge, resume,
        openWar: (warId) => setScreen({ name: 'war', warId }),
        goHome: () => setScreen({ name: 'home' }),
        openHive: () => setScreen({ name: 'hive' }),
        openWarMenu: () => setScreen({ name: 'warMenu' }),
        openHistory: () => setScreen({ name: 'history' }),
    }
    return <PrototypeContext.Provider value={value}>{children}</PrototypeContext.Provider>
}
